import { gql } from '@apollo/client/core'
import type { Ref } from 'vue'

import type { AppLocale } from '@/shared/i18n'
import { parseTag } from '../lib/boardInvariants'
import type { BoardQuery, RaidBoardQuery } from '../lib/handWrittenGraphql'
import type {
  PlayerTag,
  RaidId,
  RaidStage,
  RaidStageDiff,
  RaidStageId,
  RaidSummary,
} from '../types'
import { applyBoardSnapshot } from './board/applySnapshot'
import type { BoardSnapshotRefs } from './board/applySnapshot'
import { applyRaidBoardSnapshot } from './raid/applySnapshot'
import type { RaidBoardSnapshotRefs } from './raid/applySnapshot'
import type { RaidMapPlanSnapshot } from './raid/map'
import type { BuildPresetLibraryNode } from './buildPresets/mappers'
import { mapBuildPresetLibrary } from './buildPresets/mappers'
import type { BuildPresetStateApi } from './buildPresets/state'
import { mapRaidStageDiffs } from './raid/stageDiffs'
import {
  mapAndSortRaidSummaries,
  mapRaidStages,
  raidsInPlannerWindow,
} from './plannerStateMappers'
import type { RaidStageSnapshot, RaidSummarySnapshot } from './plannerStateMappers'

/**
 * The subscription selects the same state as the initial planner queries.
 * Each event contains a post-commit snapshot, so applying it never starts a
 * follow-up `Board`, `PlayerTags`, `RaidBoard`, `RaidMapPlan`, or calendar
 * query.
 */
export const PlannerStateChangedDocument = gql`
  subscription PlannerStateChanged($guildId: Int!, $raidId: Int, $stageId: Int) {
    boardChanged(guildId: $guildId, raidId: $raidId, stageId: $stageId) {
      type
      guildId
      entityId
      state {
        guildPlayerDiscordIds
        board {
          rooms {
            id
            stableId
            name
            accent
            noPlayerLimit
            orderIndex
          }
          groups {
            id
            stableId
            roomId
            label
            position
            maxPlayers
            collapsed
            orderIndex
          }
          players {
            player {
              id
              discordUserId
              hasSignedIn
              discordName
              guildNickname
              playerNickname
              playerAccountName
              inGameName
              role
              groupId
              groupOrderIndex
              mainWeapons
              altWeapons
              activeBuildIndex
              activeGuildBuildIndex
              tags {
                id
                guildId
                name
                seedKey
                color
              }
            }
            builds {
              id
              orderIndex
              weaponSlugs
              isAssigned
            }
            guildBuilds {
              id
              orderIndex
              weaponSlugs
              isAssigned
            }
          }
        }
        buildPresetLibrary {
          playerPresets {
            discordUserId
            presets {
              id
              name
              orderIndex
              stages { stageKind weaponSlugs }
            }
          }
          guildPresets {
            id
            name
            orderIndex
            stages { stageKind weaponSlugs }
          }
        }
        playerTags {
          id
          guildId
          name
          seedKey
          color
        }
        raids {
          id
          guildId
          name
          isPublished
          scheduledStartsAt
          durationMinutes
          cancelledAt
          completedAt
          sourceRaidId
          recurrence {
            seriesId
            parentId
            index
            total
            frequency
          }
          notes
          createdBy
          createdAt
          updatedAt
        }
        selectedStageId
        raidBoard {
          raid {
            id
            guildId
            name
            isPublished
            scheduledStartsAt
            durationMinutes
            cancelledAt
            completedAt
            sourceRaidId
            recurrence {
              seriesId
              parentId
              index
              total
              frequency
            }
            notes
            createdBy
            createdAt
            updatedAt
          }
          selectedStageId
          stageDiffs {
            stageId
            playerId
            playerLabel
            placement {
              beforeGroupId
              beforeOrderIndex
              afterGroupId
              afterOrderIndex
            }
            build {
              before {
                orderIndex
                weaponSlugs
              }
              after {
                orderIndex
                weaponSlugs
              }
              beforeActiveBuildIndex
              afterActiveBuildIndex
            }
            tags {
              added {
                id
                guildId
                name
                seedKey
                color
              }
              removed {
                id
                guildId
                name
                seedKey
                color
              }
            }
          }
          rooms {
            id
            stableId
            guildId
            raidId
            stageId
            name
            accent
            noPlayerLimit
            orderIndex
          }
          groups {
            id
            stableId
            guildId
            raidId
            stageId
            roomId
            label
            position
            maxPlayers
            collapsed
            orderIndex
          }
          players {
            player {
              id
              guildId
              raidId
              stageId
              playerId
              discordUserId
              hasSignedIn
              discordName
              guildNickname
              playerNickname
              playerAccountName
              inGameName
              role
              groupId
              groupOrderIndex
              mainWeapons
              altWeapons
              activeBuildIndex
              tags {
              id
              guildId
              name
              seedKey
              color
              }
            }
            builds {
              id
              stageId
              orderIndex
              weaponSlugs
            }
          }
        }
        raidStages {
          id
          raidId
          kind
          name
          notes
          orderIndex
        }
        raidMapPlan {
          groupPositions {
            raidGroupId
            xBasisPoints
            yBasisPoints
          }
          treePosition {
            xBasisPoints
            yBasisPoints
          }
          forestAssignments {
            id
            raidPlayerId
            locationKey
            spawnOffsetMinutes
          }
        }
      }
    }
  }
`

export interface PlannerStateSnapshot {
  readonly guildPlayerDiscordIds?: ReadonlyArray<string> | null
  readonly board: BoardQuery['board']
  readonly buildPresetLibrary: BuildPresetLibraryNode
  readonly playerTags: ReadonlyArray<{
    readonly id: number
    readonly guildId: number
    readonly name: string
    readonly seedKey: string | null
    readonly color: string
  }>
  readonly raids: ReadonlyArray<RaidSummarySnapshot>
  readonly selectedStageId: number | null
  readonly raidBoard: RaidBoardQuery['raidBoard'] | null
  readonly raidStages: ReadonlyArray<RaidStageSnapshot>
  readonly raidMapPlan: RaidMapPlanSnapshot | null
}

export interface PlannerStateChangedSubscription {
  readonly boardChanged: {
    readonly type: string
    readonly guildId: number
    readonly entityId: string | null
    readonly state: PlannerStateSnapshot | null
  }
}

export interface PlannerStateRefs {
  readonly guildPlayerDiscordIds?: Ref<ReadonlyArray<string> | null>
  readonly board: BoardSnapshotRefs
  readonly buildPresets: Pick<BuildPresetStateApi, 'applyLibrary' | 'clearLibrary'>
  readonly loading: Ref<boolean>
  readonly invalidateBoardFetch: () => void
  readonly playerTags: Ref<ReadonlyArray<PlayerTag>>
  readonly activeTagFilter: Ref<string | null>
  readonly raids: Ref<ReadonlyArray<RaidSummary>>
  readonly numericRaidId: Map<string, number>
  readonly calendarWindowStart: Ref<Date>
  readonly raidsForWindow: Ref<ReadonlyArray<RaidSummary>>
  readonly activeRaidId: Ref<RaidId | null>
  readonly activeStageId: Ref<RaidStageId | null>
  readonly raidBoard: RaidBoardSnapshotRefs
  readonly raidStages: Ref<ReadonlyArray<RaidStage>>
  readonly raidStageDiffs: Ref<ReadonlyArray<RaidStageDiff>>
  readonly stageSelectionPending: Ref<boolean>
  readonly applyRaidMapPlan: (plan: RaidMapPlanSnapshot) => void
  readonly clearRaidMapPlan: () => void
  /**
   * Reactive application locale. Required so raidsInCalendarWindow threads the locale-derived weekStartsOn into calendarWindowEnd, keeping the half-open [from, to) window aligned with the rendered grid columns. Without it the helper would fall back to its Monday-anchored default and silently mismatch a future locale that flips to Sunday-first.
   */
  readonly locale: Readonly<Ref<AppLocale>>
}

function clearSelectedRaid(refs: PlannerStateRefs): void {
  refs.activeRaidId.value = null
  refs.activeStageId.value = null
  refs.raidBoard.activeRaidBoard.value = null
  refs.raidBoard.raidRooms.value = []
  refs.raidBoard.raidGroups.value = []
  refs.raidBoard.raidPlayers.value = []
  refs.raidStages.value = []
  refs.raidStageDiffs.value = []
  refs.stageSelectionPending.value = false
  refs.clearRaidMapPlan()
}

/**
 * Drop every guild-scoped snapshot when the active capability is revoked.
 *
 * This deliberately mirrors the complete subscription state shape rather
 * than waiting for route teardown: a forbidden live stream must never leave
 * a previously hydrated board, raid, tag catalog, or tactical map visible.
 */
export function clearPlannerState(refs: PlannerStateRefs): void {
  refs.invalidateBoardFetch()
  refs.loading.value = false
  refs.board.players.value = []
  if (refs.guildPlayerDiscordIds) refs.guildPlayerDiscordIds.value = null
  refs.board.groups.value = []
  refs.board.rooms.value = []
  refs.board.numericRoomId.clear()
  refs.board.numericGroupId.clear()
  refs.buildPresets.clearLibrary()
  refs.playerTags.value = []
  refs.activeTagFilter.value = null
  refs.raids.value = []
  refs.numericRaidId.clear()
  refs.raidsForWindow.value = []
  clearSelectedRaid(refs)
}

/**
 * Commit an event's complete state in one synchronous pass. A local optimistic
 * move is intentionally overwritten too: the server snapshot is the source
 * of truth for every tab, including the tab that initiated the mutation.
 */
export function applyPlannerState(
  refs: PlannerStateRefs,
  snapshot: PlannerStateSnapshot,
): boolean {
  const selectedRaidId = refs.activeRaidId.value
  if (selectedRaidId !== null) {
    const incomingStageId = snapshot.selectedStageId === null
      ? null
      : String(snapshot.selectedStageId)
    const selectedStageWasRemoved = refs.activeStageId.value !== null
      && incomingStageId === null
      && !snapshot.raidStages.some((stage) => String(stage.id) === refs.activeStageId.value)
    if (incomingStageId !== refs.activeStageId.value && !selectedStageWasRemoved) return false
  }

  // A query started during navigation can return after this event. Mark it
  // stale before replacing refs so it cannot overwrite newer live state.
  refs.invalidateBoardFetch()
  refs.loading.value = false
  applyBoardSnapshot(refs.board, snapshot.board)
  if (refs.guildPlayerDiscordIds) {
    refs.guildPlayerDiscordIds.value = snapshot.guildPlayerDiscordIds ?? null
  }
  refs.buildPresets.applyLibrary(mapBuildPresetLibrary(snapshot.buildPresetLibrary))
  const tags = snapshot.playerTags.map(parseTag)
  refs.playerTags.value = tags
  if (
    refs.activeTagFilter.value !== null
    && !tags.some((tag) => tag.id === refs.activeTagFilter.value)
  ) {
    refs.activeTagFilter.value = null
  }

  const raids = mapAndSortRaidSummaries(snapshot.raids)
  refs.numericRaidId.clear()
  for (const raid of raids) refs.numericRaidId.set(raid.id, Number(raid.id))
  refs.raids.value = raids
  refs.raidsForWindow.value = raidsInPlannerWindow(
    raids,
    refs.calendarWindowStart.value,
    refs.locale.value,
  )

  const activeRaidId = refs.activeRaidId.value
  if (activeRaidId === null) return true
  if (!raids.some((raid) => raid.id === activeRaidId)) {
    clearSelectedRaid(refs)
    return true
  }

  const stages = mapRaidStages(snapshot.raidStages)
  refs.raidStages.value = stages
  if (
    refs.activeStageId.value !== null
    && !stages.some((stage) => stage.id === refs.activeStageId.value)
  ) {
    refs.activeStageId.value = null
  }

  const raidBoard = snapshot.raidBoard
  if (raidBoard === null || String(raidBoard.raid.id) !== activeRaidId) return true
  applyRaidBoardSnapshot(refs.raidBoard, raidBoard)
  refs.raidStageDiffs.value = mapRaidStageDiffs(raidBoard.stageDiffs, stages, raidBoard)
  refs.stageSelectionPending.value = false
  if (snapshot.raidMapPlan !== null) refs.applyRaidMapPlan(snapshot.raidMapPlan)
  return true
}
